import {
  createCustomPsetDefinitionCache,
  createCustomPsetReadActions,
  customPsetInstancesQueryKey,
  type CustomPsetDefinitionCache,
  type CustomPsetInstance,
  type CustomPsetInstanceProperty,
} from '@data-access/connect/tc-pset';
import { useConnectProject } from '@trimble/connect-project-providers';
import { t } from '@trimble/i18n';
import { asError } from '@trimble/logging';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { useLogger } from '@trimble/solidjs-utils';
import { useQuery } from '@tanstack/solid-query';
import { createEffect, createMemo, on, type Accessor } from 'solid-js';
import type {
  CustomEntityPropertySetsInput,
  CustomPanelProperty,
  CustomPropertySetInput,
} from '@trimble/ui-components/solidjs';
import { useApiServices } from './ApiServiceProvider';

export interface UseCustomPropertySetsResult {
  customEntityPropertySets: Accessor<CustomEntityPropertySetsInput[]>;
  isError: Accessor<boolean>;
  isLoading: Accessor<boolean>;
}

// Module-level definition-cache-per-region, keyed by `TcRegion.location`.
// Only the definition lookup Map is shared — read-actions themselves are
// rebuilt per query so we always use the current `getPsetApi` (avoids
// capturing a stale factory after an ApiServiceProvider remount / re-auth).
const definitionCachesByRegion = new Map<string, CustomPsetDefinitionCache>();

function getDefinitionCacheForRegion(
  regionKey: string
): CustomPsetDefinitionCache {
  const existing = definitionCachesByRegion.get(regionKey);
  if (existing !== undefined) return existing;
  const cache = createCustomPsetDefinitionCache();
  definitionCachesByRegion.set(regionKey, cache);
  return cache;
}

/**
 * @intent Loads custom property set instances for the current entity selection
 * and returns them already grouped by entity in the shape expected by
 * `ObjectPropertiesPanel`. Query errors are surfaced once per transition with
 * a logged toast.
 */
export function useCustomPropertySets(
  selectionLinks: Accessor<readonly string[]>
): UseCustomPropertySetsResult {
  const { getPsetApi } = useApiServices();
  const { selectedRegion } = useConnectProject();
  const logger = useLogger().getLogger('useCustomPropertySets');

  const query = useQuery(() => {
    const region = selectedRegion();
    const links = selectionLinks();
    const enabled = region !== undefined && links.length > 0;

    return {
      queryKey: enabled
        ? customPsetInstancesQueryKey(region.location, links)
        : (['custom-pset-instances', 'disabled'] as const),
      enabled,
      queryFn: async (): Promise<CustomPsetInstance[]> => {
        if (region === undefined) return [];
        // Rebuild the read-actions per query so we always bind the *current*
        // `getPsetApi` (avoids capturing a stale factory across
        // ApiServiceProvider remounts) while preserving the region-scoped
        // definition cache across mounts.
        const actions = createCustomPsetReadActions({
          getPsetApi: () => getPsetApi(region),
          definitionCache: getDefinitionCacheForRegion(region.location),
        });
        return actions.listInstances({ links });
      },
    };
  });

  createEffect(
    on(
      () => (query.isError ? query.error : undefined),
      (error, previousError) => {
        if (error === undefined) return;
        if (error === previousError) return;

        logger.error(
          'Failed to load custom property sets',
          { links: selectionLinks() },
          asError(error)
        );
        NotificationEventBus.emit({
          type: 'error',
          message: t('objectProperties.loadCustomPropertiesFailed'),
        });
      }
    )
  );

  const customEntityPropertySets = createMemo<CustomEntityPropertySetsInput[]>(
    () => groupInstancesByEntity(query.data ?? [])
  );

  return {
    customEntityPropertySets,
    isError: () => query.isError,
    isLoading: () => query.isFetching,
  };
}

/**
 * @intent Adapts raw {@link CustomPsetInstance}s from `@data-access/connect/tc-pset`
 * into the panel's {@link CustomEntityPropertySetsInput} shape by grouping by
 * link (entity FRN). The schema-derived type metadata is threaded through so
 * the panel can format each value by type (matching tcweb).
 *
 * Exported for unit testing.
 */
export function groupInstancesByEntity(
  instances: readonly CustomPsetInstance[]
): CustomEntityPropertySetsInput[] {
  const groupedByEntity = new Map<string, CustomPropertySetInput[]>();

  for (const instance of instances) {
    const existing = groupedByEntity.get(instance.link) ?? [];
    existing.push({
      name: instance.psetName,
      libId: instance.libId,
      defId: instance.defId,
      properties: Object.entries(instance.properties).map(([name, property]) =>
        toPanelProperty(name, property)
      ),
    });
    groupedByEntity.set(instance.link, existing);
  }

  return [...groupedByEntity.entries()].map(([entityId, psets]) => ({
    entityId,
    psets,
  }));
}

function toPanelProperty(
  name: string,
  property: CustomPsetInstanceProperty
): CustomPanelProperty {
  return {
    id: property.id,
    name,
    value: property.value,
    propType: property.propType,
    inputType: property.inputType,
    ...(property.enumOptions !== undefined
      ? { enumOptions: property.enumOptions }
      : {}),
  };
}
